import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import type { HistoryItem } from '@platform/contracts';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import {
  ProvablyFairService,
  isCrashPfRoundRevealed,
  getCrashPfClientSeedRows,
  getCrashPfSeedDisplayLabel,
  getCrashPfSeedFallbackNameKey,
  getCrashPfSeedSourceLabelKey,
  getCrashPfSeedSourceRank,
  ProvablyFairRequestError,
  type CrashPfClientSeedRow,
  type CrashPfRoundResponse,
  type CrashPfVerifyResponse,
} from '@Web/services/provablyfairservice';

import { getProvablyFairUIStyles } from '../UI/styles/provablyFairUiStyles';

import { getAssetManifest } from '@core/UI/utils/getAssetHelper';
import { ProvablyFairInfoModal } from '../UI/overlayelements/ProvablyFairInfoModal';
import { createScoreboardStyles } from '../UI/styles/ScoreBoard.styles';
import { useTheme } from '@core/UI/context/ThemeContext';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { mergeSkyxPfInfoModalChrome } from '@core/utils/skyxPfInfoModalStyles';
import { formatSkyxPlayerDisplayName } from '@core/utils/skyxPlayerDisplayName';
import {
  buildGamePublicAssetUrl,
  resolveAssetFolderSlug,
} from '@core/utils/assetFolderSlug';
import { formatDecimal, formatMultiplier, getLanguage, useT } from '@core/i18n';
import {
  formatTimeIst,
  resolveRoundEndedAtMs,
} from '@core/utils/parseTimestamp';
import { usePlayerAvatarUrl } from '@core/hooks/usePlayerAvatarUrl';
import type { ProvablyFairUIStyles } from '../UI/styles/provablyFairUiStyles';
import { SkyxNewModalPanelShell } from '@core/UI/skyx/SkyxNewModalPanelShell';
import { SkyxNewModalCloseButton } from '@core/UI/skyx/SkyxNewModalCloseButton';
import { WalletBalance } from '@core/UI/wallet/WalletBalance';
import { PF_HEADER_WALLET_RESPONSIVE_CSS } from '@core/UI/wallet/ProvablyFairHeaderResponsive.css';

// ──────────────────────────────────────────────────────────────
// Helper Functions
// ──────────────────────────────────────────────────────────────

const calculateHash = (
  serverSeed: string,
  clientSeed: string,
  nonce: number
): string => {
  const combinedString = `${serverSeed}:${clientSeed}:${nonce}`;
  return (
    'd' +
    combinedString.substring(0, 5) +
    serverSeed.substring(0, 10) +
    clientSeed.substring(0, 5) +
    nonce.toString() +
    'f00ddeadbeefc0de1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef'
  ).substring(0, 128);
};

const getColor = (x: number) => {
  if (x >= 10) return 'rgb(192, 23, 180)'; // Purple/Pink
  if (x >= 2) return 'rgb(145, 62, 248)'; // Purple
  return 'rgb(52, 180, 255)'; // Blue
};

function resolvePfSeedPlayerKey(
  row: CrashPfClientSeedRow,
  index: number
): string {
  const ref = row.ref?.trim();
  if (ref) return ref;
  const source = row.source?.trim();
  if (source) return source;
  return `pf-seed-${index}`;
}

function getPfSeedAvatarFallbackColor(playerKey: string): string {
  const colors = [
    '#e91e63',
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#009688',
    '#4caf50',
    '#ff9800',
  ];
  const hash = playerKey.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return colors[Math.abs(hash) % colors.length];
}

type PfClientSeedPlayerRowProps = {
  index: number;
  seedRow: CrashPfClientSeedRow;
  rowStyle: React.CSSProperties;
  styles: ProvablyFairUIStyles;
  playerLabel: string;
  displayName: string;
  seedLabel: string;
  resolvePlayerAvatarUrl?: (playerId: string) => string;
};

const PfClientSeedPlayerRow: React.FC<PfClientSeedPlayerRowProps> = ({
  index,
  seedRow,
  rowStyle,
  styles,
  playerLabel,
  displayName,
  seedLabel,
  resolvePlayerAvatarUrl,
}) => {
  const playerKey = resolvePfSeedPlayerKey(seedRow, index);
  const avatarUrl = usePlayerAvatarUrl(playerKey, resolvePlayerAvatarUrl);
  const fallbackLabel = displayName.trim().charAt(0).toUpperCase() || '?';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
      key={`${playerKey}-${seedRow.seed}`}
    >
      <span style={styles.playerNameLabel}>{playerLabel}</span>
      <div style={rowStyle}>
        <div style={styles.playerInfo}>
          <div style={styles.playerAvatar}>
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                style={styles.playerAvatarImage}
              />
            ) : (
              <span
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: getPfSeedAvatarFallbackColor(playerKey),
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {fallbackLabel}
              </span>
            )}
          </div>
          <span style={styles.playerId}>{displayName}</span>
        </div>
        <div style={styles.seedContainer}>
          <span style={styles.seedLabel}>{seedLabel}</span>
          <span style={styles.playerSeed}>{seedRow.seed}</span>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// Component Definition
// ──────────────────────────────────────────────────────────────

interface Props {
  roundData: HistoryItem;
  isOpen?: boolean;
  onClose: () => void;
  controller?: any;
}

export const ProvablyFairModal: React.FC<Props> = ({
  roundData,
  isOpen = true,
  onClose,
  controller,
}) => {
  const gameSlug = controller?.getGameSlug?.() || getGameFromUrl() || 'crash';
  const platformUIConfig = controller?.getPlatformUIConfig?.() || {};
  const pfRealEnabled = Boolean(platformUIConfig.provablyFairRealMode);
  const pfSectionChrome = platformUIConfig.provablyFairRoundSectionChrome;
  const t = useT(platformUIConfig.provablyFairI18nGameId);

  // 1. Get Styles (optional game-specific overrides, e.g. skyx_new)
  const styles = useMemo(() => {
    const base = getProvablyFairUIStyles();
    const overrides = platformUIConfig.provablyFairStyleOverrides;
    if (!overrides) return base;
    const merged: ProvablyFairUIStyles = { ...base };
    for (const key of Object.keys(
      overrides
    ) as (keyof ProvablyFairUIStyles)[]) {
      const patch = overrides[key];
      if (!patch) continue;
      const existing = merged[key];
      if (existing && typeof existing === 'object') {
        (merged as unknown as Record<string, React.CSSProperties>)[key] = {
          ...existing,
          ...patch,
        };
      } else {
        (merged as unknown as Record<string, React.CSSProperties>)[key] = patch;
      }
    }
    return merged;
  }, [platformUIConfig.provablyFairStyleOverrides]);

  // 2. Device Detection
  const deviceType = controller
    ? controller.getGameState().deviceType
    : typeof window !== 'undefined' && window.innerWidth <= 768
      ? 'mobile'
      : 'desktop';
  const isLandscape = controller
    ? controller.getGameState().isLandscape
    : typeof window !== 'undefined' && window.innerWidth > window.innerHeight;
  const isHandheldLandscape =
    platformUIConfig.resolveProvablyFairHandheldLandscape?.(
      deviceType,
      isLandscape
    ) ?? false;
  const handheldLandscapeLayout = isHandheldLandscape
    ? platformUIConfig.provablyFairHandheldLandscapeConfig
    : undefined;

  const { theme } = useTheme();

  // 3. Animation States
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ANIMATION_DURATION = 300;

  // 4. Animation Effect
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      timeoutIdRef.current = setTimeout(() => {
        setIsAnimatingIn(true);
      }, 10);
    } else if (shouldRender) {
      setIsAnimatingIn(false);
      timeoutIdRef.current = setTimeout(() => {
        setShouldRender(false);
      }, ANIMATION_DURATION);
    }
    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
  }, [isOpen, shouldRender]);

  // 5. Data Logic
  const primaryClientSeed = 'RkEZsB6KPMLen7tBY42x';
  const roundNonce = Number(roundData.roundId);
  const calculatedHash = useMemo(() => {
    return calculateHash(roundData.serverSeed, primaryClientSeed, roundNonce);
  }, [roundData.serverSeed, primaryClientSeed, roundNonce]);

  const LEGACY_DECIMAL_PLACEHOLDER = '1989645396603539';

  const locale = getLanguage() === 'es' ? 'es-IN' : 'en-IN';

  // 6. Asset Logic
  const manifest = useMemo(
    () => getAssetManifest(gameSlug, controller?.getAssetManifest?.()),
    [controller, gameSlug]
  );
  const useGameStore = resolveGameStoreHook(controller);
  const gameData = useGameStore((s: any) => ({ assetManager: s.assetManager }));
  const assetManager =
    controller?.getGameState?.()?.assetManager || gameData.assetManager;
  const [showProvablyFairInfo, setShowProvablyFairInfo] = useState(false);

  useEffect(() => {
    if (!isOpen) setShowProvablyFairInfo(false);
  }, [isOpen]);

  // ── Real Provably Fair audit + server-side verification (skyx_new only) ──
  // Other games keep the existing display and no Verify flow, so this is scoped.
  const pfService = useMemo(() => ProvablyFairService.getInstance(), []);
  const pfGameCode = controller?.getGameState?.()?.gameId;
  const pfTableNo = controller?.getGameState?.()?.tableId;
  const pfRoundId = Number(roundData.roundId);

  const [pfAudit, setPfAudit] = useState<CrashPfRoundResponse | null>(null);
  const [pfAuditError, setPfAuditError] = useState<string | null>(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfVerifyResult, setPfVerifyResult] =
    useState<CrashPfVerifyResponse | null>(null);
  const [pfVerifying, setPfVerifying] = useState(false);
  const [pfVerifyError, setPfVerifyError] = useState<string | null>(null);

  useEffect(() => {
    if (!pfRealEnabled || !isOpen) return;
    if (
      pfGameCode == null ||
      pfTableNo == null ||
      !Number.isFinite(pfRoundId)
    ) {
      return;
    }
    let cancelled = false;
    setPfLoading(true);
    setPfAuditError(null);
    setPfVerifyResult(null);
    setPfVerifyError(null);
    pfService
      .getRound(pfGameCode, pfTableNo, pfRoundId)
      .then(res => {
        if (!cancelled) setPfAudit(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPfAudit(null);
        const status =
          err instanceof ProvablyFairRequestError ? err.status : undefined;
        setPfAuditError(
          status === 404
            ? t('ui.popuppanel.provably_fair.verified_fail')
            : t('ui.popuppanel.provably_fair.verify_error')
        );
      })
      .finally(() => {
        if (!cancelled) setPfLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pfRealEnabled, isOpen, pfGameCode, pfTableNo, pfRoundId, pfService, t]);

  const pfRevealed = isCrashPfRoundRevealed(pfAudit);

  const roundEndedAtMs = useMemo(
    () =>
      resolveRoundEndedAtMs(
        pfAudit?.revealedAt,
        pfAudit?.frozenAt,
        roundData.timeStamp
      ),
    [pfAudit?.revealedAt, pfAudit?.frozenAt, roundData.timeStamp]
  );

  const timeIST = useMemo(
    () =>
      pfLoading && roundEndedAtMs == null
        ? t('ui.popuppanel.provably_fair.loading')
        : formatTimeIst(roundEndedAtMs, locale),
    [locale, pfLoading, roundEndedAtMs, t]
  );

  const handlePfVerify = useCallback(async () => {
    if (!pfAudit || !isCrashPfRoundRevealed(pfAudit)) return;
    setPfVerifying(true);
    setPfVerifyError(null);
    setPfVerifyResult(null);
    try {
      const res = await pfService.verifyRoundFromAudit(pfAudit);
      setPfVerifyResult(res);
    } catch {
      setPfVerifyError(t('ui.popuppanel.provably_fair.verify_error'));
    } finally {
      setPfVerifying(false);
    }
  }, [pfAudit, pfService, t]);

  const assetFolder =
    controller?.getAssetBaseSlug?.() ??
    resolveAssetFolderSlug(gameSlug) ??
    gameSlug;

  const pfInfoModalScoreboardStyles = useMemo(() => {
    const base = createScoreboardStyles(theme, deviceType, false);
    return isSkyxNewRoute(gameSlug) ? mergeSkyxPfInfoModalChrome(base) : base;
  }, [theme, deviceType, gameSlug]);

  const pfInfoModalI18nGameId = platformUIConfig.provablyFairI18nGameId;

  const pfInfoHeaderIconUrl = useMemo(
    () =>
      platformUIConfig.resolveProvablyFairInfoHeaderIconUrl?.(
        assetManager?.baseUrl
      ),
    [platformUIConfig, assetManager?.baseUrl]
  );

  const getImage = useCallback(
    (key: string) => {
      const value = (manifest.images as Record<string, string>)[key];
      if (!value) return '';
      return (
        controller?.getAssetUrl?.(value) ??
        buildGamePublicAssetUrl(assetFolder, value, assetManager?.baseUrl)
      );
    },
    [manifest, controller, assetFolder, assetManager?.baseUrl]
  );

  const resolvePlayerAvatarUrl = useCallback(
    (playerId: string): string => {
      const avatarPath = controller?.getPlayerAvatarUrl?.(playerId) ?? '';
      if (!avatarPath) return '';
      if (/^(https?:\/\/|data:image\/|blob:)/i.test(avatarPath)) {
        return avatarPath;
      }
      return (
        controller?.getAssetUrl?.(avatarPath) ??
        buildGamePublicAssetUrl(assetFolder, avatarPath, assetManager?.baseUrl)
      );
    },
    [assetFolder, assetManager?.baseUrl, controller]
  );

  const serverIconUrl = getImage('serverIcon');
  const clientIconUrl = getImage('clientIcon');
  const hashIconUrl = getImage('hashIcon');
  const crossNormalUrl = getImage('modalCloseNormal');
  const crossHoverUrl = getImage('modalCloseOver');
  const crossActiveUrl = getImage('modalCloseDown');

  // Player Avatars (demo rows — use the same CDN resolver as live gameplay)
  const player1Id = 'player-d0';
  const player2Id = 'player-d3';
  const player3Id = 'player-d7';
  const player1Avatar = resolvePlayerAvatarUrl(player1Id);
  const player2Avatar = resolvePlayerAvatarUrl(player2Id);
  const player3Avatar = resolvePlayerAvatarUrl(player3Id);

  // 7. Close button hover/active state
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isCloseActive, setIsCloseActive] = useState(false);
  const [isContentScrolled, setIsContentScrolled] = useState(false);

  // 8. Dynamic Style Generation
  const getModalStyle = useCallback(() => {
    let baseStyle: React.CSSProperties;

    if (deviceType === 'mobile') {
      baseStyle = styles.modalMobile;
    } else if (deviceType === 'tablet') {
      baseStyle = styles.modalTablet;
    } else {
      baseStyle = styles.modal;
    }

    const initialTransform = isHandheldLandscape
      ? 'translateY(-12px)'
      : deviceType === 'mobile'
        ? 'translateY(-100%)'
        : 'translateY(-50px)';

    const endTransform = isHandheldLandscape
      ? 'translateY(0)'
      : deviceType === 'mobile'
        ? 'translateY(0)'
        : 'none';

    return {
      ...baseStyle,
      ...(handheldLandscapeLayout?.modal ?? {}),
      transform: isAnimatingIn ? endTransform : initialTransform,
      opacity: isAnimatingIn ? 1 : 0,
      transition: `all ${ANIMATION_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
    };
  }, [
    deviceType,
    styles,
    isAnimatingIn,
    isHandheldLandscape,
    handheldLandscapeLayout?.modal,
  ]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 8. Early Return Check
  // hooks already executed above; conditional return only for rendering
  if (!shouldRender) return null;

  // 9. Layout Helpers
  const isMobile = deviceType === 'mobile';
  const currentPlayerRowStyle = isMobile
    ? styles.playerRowMobile
    : styles.playerRow;
  const currentResultGridStyle = isMobile
    ? styles.resultGridMobile
    : styles.resultGrid;

  // Helper to render a player row with consistent alignment
  const renderPlayerRow = (
    label: string,
    avatar: string,
    id: string,
    seed: string
  ) => (
    <div style={currentPlayerRowStyle} key={id + seed}>
      <div style={styles.playerInfo}>
        <span style={styles.playerNameLabel}>{label}</span>
        <div style={styles.playerAvatar}>
          {avatar ? (
            <img src={avatar} alt={label} style={styles.playerAvatarImage} />
          ) : (
            <span
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: getPfSeedAvatarFallbackColor(id),
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {id.trim().charAt(0).toUpperCase() || '?'}
            </span>
          )}
        </div>
        <span style={styles.playerId}>{id}</span>
      </div>
      <div style={styles.seedContainer}>
        {/* Render "Seed:" label separately for color control */}
        <span style={styles.seedLabel}>
          {t('ui.popuppanel.provably_fair.seed_colon')}
        </span>
        <span style={styles.playerSeed}>{seed}</span>
      </div>
    </div>
  );

  // Small PASS/FAIL row for the verify result panel.
  const renderCheck = (label: string, ok: boolean) => (
    <span key={label} style={{ color: ok ? '#8fe36b' : '#ff6b6b' }}>
      {ok ? '✓' : '✗'} {label}:{' '}
      {ok
        ? t('ui.popuppanel.provably_fair.pass')
        : t('ui.popuppanel.provably_fair.fail')}
    </span>
  );

  // Real values fill the existing layout when the round audit is available;
  // otherwise the previous display is preserved (SEC-001/002: no pre-reveal leak).
  const serverSeedDisplay = !pfRealEnabled
    ? roundData.serverSeed
    : pfRevealed
      ? pfAudit!.serverSeedRevealed
      : pfAudit
        ? t('ui.popuppanel.provably_fair.pending_reveal')
        : roundData.serverSeed;

  const hmacDisplay =
    pfRealEnabled && pfRevealed && pfAudit?.hmacHex
      ? pfAudit.hmacHex
      : calculatedHash;
  const hexDisplay = hmacDisplay.substring(0, 13);
  const multiplierDisplay =
    pfRealEnabled && pfAudit?.multiplier != null
      ? pfAudit.multiplier
      : roundData.bust;
  const bustColor = getColor(multiplierDisplay);

  const isUsableServerSeedHash = (value?: string | null) => {
    const trimmed = value?.trim();
    if (!trimmed) return false;
    return !/^(n\/a|error)/i.test(trimmed);
  };

  const serverSeedHashDisplay = pfRealEnabled
    ? isUsableServerSeedHash(pfAudit?.serverSeedHash)
      ? pfAudit!.serverSeedHash.trim()
      : isUsableServerSeedHash(roundData.serverSeedHash)
        ? roundData.serverSeedHash.trim()
        : pfLoading
          ? t('ui.popuppanel.provably_fair.loading')
          : '—'
    : LEGACY_DECIMAL_PLACEHOLDER;

  const realSeedRows =
    pfRealEnabled && pfRevealed && pfAudit
      ? getCrashPfClientSeedRows(pfAudit)
      : [];
  const useRealSeedRows = realSeedRows.length > 0;
  const showPfPendingSeeds =
    pfRealEnabled && !!pfAudit && !pfRevealed && !pfLoading;

  const sectionTitle = (color: string) =>
    pfRealEnabled && pfSectionChrome
      ? pfSectionChrome.styleSectionTitle(styles.infoTitle, color)
      : styles.infoTitle;

  const sectionIcon = (filter?: string) =>
    pfRealEnabled && pfSectionChrome
      ? pfSectionChrome.styleSectionIcon(styles.infoSectionImage, filter)
      : styles.infoSectionImage;

  const modalPanelStyle = getModalStyle();
  const useSkyxPfShell = Boolean(styles.closeIcon);

  const modal = (
    <div
      style={{
        ...styles.overlay,
        ...(handheldLandscapeLayout?.overlay ?? {}),
        opacity: isAnimatingIn ? 1 : 0,
        transition: `opacity ${ANIMATION_DURATION}ms ease-out`,
      }}
      data-skyx-modal-backdrop
      onClick={handleOverlayClick}
    >
      <SkyxNewModalPanelShell
        enabled={useSkyxPfShell}
        panelStyle={modalPanelStyle}
        onClick={e => e.stopPropagation()}
      >
        {/* HEADER */}
        <style>{PF_HEADER_WALLET_RESPONSIVE_CSS}</style>
        <div
          data-pf-header
          style={{
            ...styles.header,
            position: 'relative',
            zIndex: 2,
            flexWrap: 'wrap-reverse',
            rowGap: '8px',
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
            ...(isContentScrolled
              ? (styles.headerScrollActive ?? {
                  boxShadow: '0 4px 6px -2px rgba(0,0,0,0.18)',
                })
              : {}),
          }}
        >
          <div style={styles.headerContent}>
            <h2 style={styles.title}>
              {t('ui.popuppanel.provably_fair.round_title', {
                id: roundData.roundId,
              })}
            </h2>

            {/* UPDATED: Applied dynamic color to Header Bust only */}
            <span style={{ ...styles.headerBust, color: bustColor }}>
              {formatMultiplier(multiplierDisplay)}
            </span>

            <span style={styles.headerTime}>{timeIST}</span>
          </div>

          {/* WALLET BALANCE + CROSS (close) button */}
          <div
            data-pf-header-actions
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginLeft: 'auto',
            }}
          >
            <WalletBalance variant="modal" />
            {styles.closeIcon ? (
              <SkyxNewModalCloseButton
                onClick={onClose}
                ariaLabel={t('ui.popuppanel.provably_fair.close_modal')}
                mobile={deviceType !== 'desktop'}
                variant="chip"
              />
            ) : (
              <button
                className={undefined}
                style={{
                  ...styles.closeButton,
                  ...(isCloseHovered && styles.closeButtonHover
                    ? styles.closeButtonHover
                    : {}),
                }}
                onClick={onClose}
                onMouseEnter={() => setIsCloseHovered(true)}
                onMouseLeave={() => {
                  setIsCloseHovered(false);
                  setIsCloseActive(false);
                }}
                onMouseDown={() => setIsCloseActive(true)}
                onMouseUp={() => setIsCloseActive(false)}
                aria-label={t('ui.popuppanel.provably_fair.close_modal')}
              >
                <img
                  src={
                    isCloseActive
                      ? crossActiveUrl
                      : isCloseHovered
                        ? crossHoverUrl
                        : crossNormalUrl
                  }
                  alt={t('ui.common.close')}
                  style={{
                    width: '20px',
                    height: '20px',
                    display: 'block',
                    lineHeight: 5,
                    transition: 'transform 0.1s ease',
                    transform: isCloseActive ? 'scale(0.92)' : 'scale(1)',
                    marginTop: 5,
                  }}
                />
              </button>
            )}
          </div>
        </div>

        {/* CONTENT */}
        <div
          className="bet-list-scrollbar"
          onScroll={e => setIsContentScrolled(e.currentTarget.scrollTop > 0)}
          style={{
            ...styles.contentArea,
            ...(handheldLandscapeLayout?.contentArea ?? {}),
          }}
        >
          <div style={styles.contentInner}>
            {/* Server Seed */}
            <div style={styles.infoSection}>
              <div style={styles.infoSectionHeader}>
                <img
                  src={serverIconUrl}
                  alt={t('ui.popuppanel.provably_fair.server_icon_alt')}
                  style={sectionIcon()}
                />
                <div style={styles.infoTextGroup}>
                  <span
                    style={sectionTitle(pfSectionChrome?.colors.server ?? '')}
                  >
                    {t('ui.popuppanel.provably_fair.server_seed_title')}
                  </span>
                  <span style={styles.infoSubTitle}>
                    {t('ui.popuppanel.provably_fair.server_seed_subtitle')}
                  </span>
                </div>
              </div>
              <div style={styles.infoValueBox}>{serverSeedDisplay}</div>
            </div>

            {/* Client Seed Section */}
            <div style={styles.infoSection}>
              <div style={styles.infoSectionHeader}>
                <img
                  src={clientIconUrl}
                  alt={t('ui.popuppanel.provably_fair.client_icon_alt')}
                  style={sectionIcon()}
                />
                <div style={styles.infoTextGroup}>
                  <span
                    style={sectionTitle(pfSectionChrome?.colors.client ?? '')}
                  >
                    {t('ui.popuppanel.provably_fair.client_seed_title')}
                  </span>
                  <span style={styles.infoSubTitle}>
                    {t('ui.popuppanel.provably_fair.client_seed_subtitle')}
                  </span>
                </div>
              </div>
              <div style={styles.clientSeedList}>
                {pfRealEnabled && pfLoading ? (
                  <div style={styles.infoValueBox}>
                    {t('ui.popuppanel.provably_fair.loading')}
                  </div>
                ) : useRealSeedRows ? (
                  realSeedRows.map((s, i) => {
                    const fallbackNameKey = getCrashPfSeedFallbackNameKey(s);
                    const rawDisplayName = fallbackNameKey
                      ? t(fallbackNameKey)
                      : getCrashPfSeedDisplayLabel(s);
                    const displayName =
                      !fallbackNameKey && isSkyxNewRoute(gameSlug)
                        ? formatSkyxPlayerDisplayName(rawDisplayName)
                        : rawDisplayName;
                    return (
                      <PfClientSeedPlayerRow
                        key={`${resolvePfSeedPlayerKey(s, i)}-${s.seed}`}
                        index={i}
                        seedRow={s}
                        rowStyle={currentPlayerRowStyle}
                        styles={styles}
                        playerLabel={t(getCrashPfSeedSourceLabelKey(s.source), {
                          num: getCrashPfSeedSourceRank(realSeedRows, i),
                        })}
                        displayName={displayName}
                        seedLabel={t('ui.popuppanel.provably_fair.seed_colon')}
                        resolvePlayerAvatarUrl={resolvePlayerAvatarUrl}
                      />
                    );
                  })
                ) : showPfPendingSeeds ? (
                  <div style={styles.infoValueBox}>
                    {t('ui.popuppanel.provably_fair.pending_reveal')}
                  </div>
                ) : pfRealEnabled && pfAuditError ? (
                  <div style={styles.infoValueBox}>{pfAuditError}</div>
                ) : pfRealEnabled && !pfAudit && !pfLoading ? (
                  <div style={styles.infoValueBox}>
                    {t('ui.popuppanel.provably_fair.verified_fail')}
                  </div>
                ) : pfRealEnabled ? null : (
                  [
                    renderPlayerRow(
                      t('ui.popuppanel.provably_fair.player_n_label', {
                        num: 1,
                      }),
                      player1Avatar,
                      'd***7',
                      'RkEZsB6KPMLen7tBY42x'
                    ),
                    renderPlayerRow(
                      t('ui.popuppanel.provably_fair.player_n_label', {
                        num: 2,
                      }),
                      player2Avatar,
                      'd***0',
                      'I2yfyD5o9NS2pog984bb'
                    ),
                    renderPlayerRow(
                      t('ui.popuppanel.provably_fair.player_n_label', {
                        num: 3,
                      }),
                      player3Avatar,
                      'd***4',
                      'Rt9EtxqnjDieTXR6dOnK'
                    ),
                  ]
                )}
              </div>
            </div>

            {/* Combined Hash */}
            <div style={styles.infoSection}>
              <div style={styles.infoSectionHeader}>
                <img
                  src={hashIconUrl}
                  alt={t('ui.popuppanel.provably_fair.hash_icon_alt')}
                  style={sectionIcon()}
                />
                <div style={styles.infoTextGroup}>
                  <span
                    style={sectionTitle(pfSectionChrome?.colors.hash ?? '')}
                  >
                    {t('ui.popuppanel.provably_fair.combined_hash_title')}
                  </span>
                  <span style={styles.infoSubTitle}>
                    {t('ui.popuppanel.provably_fair.combined_hash_subtitle')}
                  </span>
                </div>
              </div>
              <div style={styles.infoValueBox}>{hmacDisplay}</div>
            </div>

            {/* Result Grid */}
            <div style={styles.resultSection}>
              {pfRealEnabled && (
                <div
                  style={{
                    ...styles.infoSectionHeader,
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  {pfSectionChrome?.renderGameResultIcon?.(32)}
                  <div style={styles.infoTextGroup}>
                    <span
                      style={sectionTitle(pfSectionChrome?.colors.result ?? '')}
                    >
                      {t('ui.popuppanel.provably_fair.game_result_title')}
                    </span>
                  </div>
                </div>
              )}
              <div style={currentResultGridStyle}>
                {/* Hex */}
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>
                    {t('ui.popuppanel.provably_fair.hex_label')}
                  </span>
                  <span style={styles.resultValue}>{hexDisplay}</span>
                </div>

                {/* Server seed commitment (skyx_new) / legacy decimal placeholder */}
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>
                    {t('ui.popuppanel.provably_fair.decimal_label')}
                  </span>
                  <span style={styles.resultValue}>
                    {serverSeedHashDisplay}
                  </span>
                </div>

                {/* Result */}
                <div style={styles.resultBox}>
                  <span style={styles.resultLabel}>
                    {pfRealEnabled
                      ? t('ui.popuppanel.provably_fair.final_multiplier_label')
                      : `${t('ui.scoreboard.result')}:`}
                  </span>

                  {/* KEPT WHITE (default style) */}
                  <span style={styles.resultValue}>
                    {formatDecimal(multiplierDisplay)}
                  </span>
                </div>
              </div>
            </div>

            {/* VERIFY (skyx_new) — server-side Provably Fair verification */}
            {pfRealEnabled && (
              <div style={styles.infoSection}>
                <button
                  type="button"
                  data-skyx-sound="bet"
                  data-skyx-pf-verify
                  onClick={handlePfVerify}
                  disabled={!pfRevealed || pfVerifying}
                  style={{
                    alignSelf: 'flex-start',
                    padding: '8px 22px',
                    borderRadius: 12,
                    border: '1px solid transparent',
                    background:
                      'linear-gradient(135deg, #6A00FF 0%, #B347FF 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: 14,
                    fontFamily: "'Roboto', sans-serif",
                    cursor:
                      !pfRevealed || pfVerifying ? 'not-allowed' : 'pointer',
                    opacity: !pfRevealed || pfVerifying ? 0.5 : 1,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  {pfVerifying
                    ? t('ui.popuppanel.provably_fair.verifying')
                    : t('ui.popuppanel.provably_fair.verify_button')}
                </button>

                {pfLoading && (
                  <span style={{ color: '#c9c4d6', fontSize: 12 }}>
                    {t('ui.popuppanel.provably_fair.loading')}
                  </span>
                )}
                {!pfLoading && pfAudit && !pfRevealed && (
                  <span style={{ color: '#f2c14e', fontSize: 12 }}>
                    {t('ui.popuppanel.provably_fair.pending_reveal')}
                  </span>
                )}
                {pfAuditError && (
                  <span style={{ color: '#ff6b6b', fontSize: 12 }}>
                    {pfAuditError}
                  </span>
                )}
                {pfVerifyError && (
                  <span style={{ color: '#ff6b6b', fontSize: 12 }}>
                    {pfVerifyError}
                  </span>
                )}

                {pfVerifyResult && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      fontSize: 12,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: pfVerifyResult.valid ? '#5ce712' : '#ff6b6b',
                      }}
                    >
                      {pfVerifyResult.valid
                        ? t('ui.popuppanel.provably_fair.verified_pass')
                        : t('ui.popuppanel.provably_fair.verified_fail')}
                    </span>
                    {renderCheck(
                      t('ui.popuppanel.provably_fair.check_server_seed_hash'),
                      pfVerifyResult.serverSeedHashValid
                    )}
                    {renderCheck(
                      t('ui.popuppanel.provably_fair.check_hmac'),
                      pfVerifyResult.hmacValid
                    )}
                    {renderCheck(
                      t('ui.popuppanel.provably_fair.check_multiplier'),
                      pfVerifyResult.multiplierValid
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div style={styles.modalFooter}>
          {t('ui.popuppanel.provably_fair.instructions_prefix')}{' '}
          <span
            style={styles.modalFooterLink}
            onClick={e => {
              e.stopPropagation();
              setShowProvablyFairInfo(true);
            }}
          >
            {t('ui.popuppanel.provably_fair.what_is_link')}
          </span>
        </div>

        {/* Nested Info Modal */}
        {showProvablyFairInfo && (
          <ProvablyFairInfoModal
            isOpen={showProvablyFairInfo}
            onClose={() => setShowProvablyFairInfo(false)}
            assetManager={assetManager}
            styles={pfInfoModalScoreboardStyles}
            i18nGameId={pfInfoModalI18nGameId}
            headerIconUrl={pfInfoHeaderIconUrl}
            diagramOverride={platformUIConfig.renderProvablyFairInfoDiagram?.({
              assetManager,
            })}
          />
        )}
      </SkyxNewModalPanelShell>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }
  return modal;
};
